'use client';

import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { type VolunteerLocationInfo, type VolunteerSearchFilters, buildVolunteerSearchParams, resolveVolunteerListIdentity, resolveVolunteerListView, resolveVolunteerSearchTrack } from './datalayer-volunteer.ts';
import { trackVolunteerSearch, trackVolunteerViewContentList } from './datalayer.tsx';
export interface TrackVolunteerListProps {
  page: number;
  /** Pass the stable `data.items` reference, not a fresh slice, so effects only re-run on real data changes. */
  items: VolunteerLocationInfo[] | undefined;
  limit: number;
  sortBy: string;
  query: string;
  isFetching: boolean;
  /** Gate for view_content_list: hold the impression until the caller's list
   * has settled. Defaults to true for callers with no async location. */
  ready?: boolean;
  slug?: string;
  /** Tour display name, used to build the tour listing's content_list_name. */
  tourName?: string | null;
}

/**
 * Null-rendering `Track*` component that fires volunteer list impressions:
 * `view_content_list` (once per distinct list) and `volunteer_search` (on
 * sort/query change). Pure decision logic lives in `datalayer-volunteer.ts`.
 */
export const TrackVolunteerList: FC<TrackVolunteerListProps> = ({
  page,
  items,
  limit,
  sortBy,
  query,
  isFetching,
  ready = true,
  slug,
  tourName
}) => {
  const lastTrackedSignatureRef = useRef<string | null>(null);

  // Reset dedupe on navigation: the client instance can survive App Router
  // navigations, and re-entering the list is a genuine new view.
  const pathname = usePathname();
  const lastPathnameRef = useRef(pathname);
  if (lastPathnameRef.current !== pathname) {
    lastPathnameRef.current = pathname;
    lastTrackedSignatureRef.current = null;
  }

  // The sort/query last reported to volunteer_search. Seeded with the mount
  // values so nuqs syncing URL defaults on mount isn't mistaken for a user
  // change; updated whenever a search fires so reverting a filter to its
  // previous value doesn't refire.
  const lastTrackedFiltersRef = useRef<VolunteerSearchFilters>({
    sortBy,
    query
  });
  const locations = (items ?? []).slice(0, limit);

  // Fire view_content_list once results arrive, held until `ready` so
  // transient results during location resolution don't each fire an impression.
  useEffect(() => {
    if (!ready) {
      return;
    }
    const view = resolveVolunteerListView(page, locations, lastTrackedSignatureRef.current);
    if (view) {
      lastTrackedSignatureRef.current = view.signature;
      const identity = resolveVolunteerListIdentity({
        slug,
        tourName
      });
      trackVolunteerViewContentList({
        locations,
        contentListId: identity.contentListId,
        contentListName: identity.contentListName
      });
    }

    // Keyed on the stable `items` ref, not the derived slice.
  }, [items, limit, page, slug, tourName, ready]);

  // Fire volunteer_search once results for a new filter set have settled.
  //
  // A single settled-state effect replaces the earlier queue/flush/fallback
  // split: while `isFetching` is false, `items` already holds the results for
  // the current filters, so this covers both the refetch case (fires when new
  // items land) and the no-refetch case (e.g. sort by location without geo,
  // where the query key is unchanged and fetching never toggles).
  useEffect(() => {
    if (isFetching) {
      return;
    }
    const changed = resolveVolunteerSearchTrack({
      sortBy,
      query
    }, lastTrackedFiltersRef.current);
    if (!changed) {
      return;
    }
    lastTrackedFiltersRef.current = changed;
    trackVolunteerSearch(buildVolunteerSearchParams({
      sortBy,
      query,
      slug,
      tourName,
      locations
    }));
    // `items` is in deps so the effect re-evaluates when results land; the
    // derived `locations` slice is read at fire time.
  }, [isFetching, sortBy, query, items, limit, slug, tourName]);
  return null;
};